import type { ReactNode } from "react";
import type { Currency, StrategyPnl, StrategyStatus } from "@richrich/contracts";
import { Card } from "../primitives/Card";
import { Badge, type BadgeTone } from "../primitives/Badge";
import { Skeleton } from "../primitives/Skeleton";
import { Sparkline } from "./Sparkline";
import { paletteFor } from "../lib/palette";
import { useResolvedTheme, type Theme } from "../lib/theme";
import { cn } from "../lib/cn";
import { decimalSign, formatMoney, formatPercent } from "../lib/format";

export interface StrategyPnLGridItem {
  id: string;
  name: string;
  /** Performance stats from `GET /api/strategies/:id/pnl`. */
  pnl: StrategyPnl;
  baseCurrency: Currency;
  /** Optional realized-P&L trajectory for the sparkline (oldest → newest). */
  history?: number[];
  status?: StrategyStatus;
}

export interface StrategyPnLGridProps {
  items: StrategyPnLGridItem[];
  theme?: Theme;
  loading?: boolean;
  /** Number of skeleton tiles while loading. */
  skeletonCount?: number;
  onSelect?: (id: string) => void;
  title?: ReactNode;
  className?: string;
}

const STATUS_TONE: Record<StrategyStatus, BadgeTone> = {
  draft: "neutral",
  active: "good",
  paused: "warning",
  halted: "critical",
  archived: "neutral",
};

/**
 * Per-strategy P&L overview (ARCHITECTURE §8): a responsive grid of tiles, each
 * pairing realized P&L / win-rate with a sparkline of the strategy's realized
 * trajectory. P&L color is paired with a sign so direction is never color-alone.
 */
export function StrategyPnLGrid({
  items,
  theme,
  loading = false,
  skeletonCount = 4,
  onSelect,
  title = "Strategy P&L",
  className,
}: StrategyPnLGridProps): ReactNode {
  const resolved = useResolvedTheme(theme);
  const palette = paletteFor(resolved);

  return (
    <Card title={title} className={className}>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {loading ? (
          Array.from({ length: skeletonCount }, (_, i) => (
            <div key={i} className="rounded-lg border border-hairline p-3">
              <Skeleton width={120} height={14} rounded />
              <Skeleton className="mt-3" width={90} height={22} />
              <Skeleton className="mt-3" height={28} />
            </div>
          ))
        ) : items.length === 0 ? (
          <div className="col-span-full rounded-lg border border-dashed border-hairline py-8 text-center text-sm text-muted">
            No strategies yet
          </div>
        ) : (
          items.map((item) => {
            const sign = decimalSign(item.pnl.realizedPnl);
            const pnlColorClass =
              sign > 0 ? "text-good-text" : sign < 0 ? "text-critical" : "text-ink";
            const interactive = onSelect !== undefined;
            return (
              <div
                key={item.id}
                role={interactive ? "button" : undefined}
                tabIndex={interactive ? 0 : undefined}
                onClick={interactive ? () => onSelect(item.id) : undefined}
                onKeyDown={
                  interactive
                    ? (e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          onSelect(item.id);
                        }
                      }
                    : undefined
                }
                className={cn(
                  "rounded-lg border border-hairline bg-surface p-3 transition-colors",
                  interactive &&
                    "cursor-pointer hover:border-brand/40 focus:outline-none focus-visible:ring-2 focus-visible:ring-brand/40",
                )}
              >
                <div className="flex items-center justify-between gap-2">
                  <span className="truncate text-sm font-medium text-ink">{item.name}</span>
                  {item.status !== undefined && (
                    <Badge tone={STATUS_TONE[item.status]} dot>
                      {item.status}
                    </Badge>
                  )}
                </div>

                <div className="mt-2 flex items-end justify-between gap-2">
                  <div>
                    <div className={cn("text-lg font-semibold tabular-nums", pnlColorClass)}>
                      {formatMoney(item.pnl.realizedPnl, item.baseCurrency, { signed: true })}
                    </div>
                    <div className="mt-0.5 text-xs text-muted">
                      {item.pnl.trades} trades · {item.pnl.wins}W/{item.pnl.losses}L ·{" "}
                      {item.pnl.winRate !== null
                        ? `${formatPercent(item.pnl.winRate, 0)} win`
                        : "no wins yet"}
                    </div>
                  </div>
                  {item.history !== undefined && item.history.length > 1 ? (
                    <Sparkline
                      data={item.history}
                      theme={resolved}
                      width={88}
                      height={30}
                      color="auto"
                    />
                  ) : (
                    <div
                      className="h-[2px] w-[88px] rounded-full"
                      style={{ backgroundColor: palette.grid }}
                      aria-hidden="true"
                    />
                  )}
                </div>
              </div>
            );
          })
        )}
      </div>
    </Card>
  );
}
